// main tools
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import Head from 'next/head'

// dayjs
import { locales } from '@centria-ui/dayjs/locales'
import '@centria-ui/dayjs/load-plugins'
import dayjs from 'dayjs'

// components
import { Spinner } from '@centria-ui-atoms/spinner'

// prime components
import { BlockUI } from 'primereact/blockui'

// providers
import { AppContextProvider } from '@centria-ui/contexts/app/provider'
import { PrimeReactProvider } from 'primereact/api'
import { SessionProvider } from 'next-auth/react'

// styles
import 'primereact/resources/themes/lara-light-indigo/theme.css'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'

// types
import type { SessionProviderProps } from 'next-auth/react'
import type { FC, ReactNode } from 'react'
import type { Session } from 'next-auth'

interface LayoutProps extends SessionProviderProps {
  title: string
  session: Session
  children: ReactNode
  logoSpinner?: JSX.Element
}

export const AppLayout: FC<LayoutProps> = ({
  title,
  session,
  children,
  logoSpinner,
  refetchInterval
}) => {
  const { locale, events } = useRouter()
  const [loadingPage, setLoadingPage] = useState(false)

  /**
   * set dayjs current locale
   */
  useEffect(() => {
    ;(async () => {
      const currentLocale = locales[locale as keyof typeof locales] ?? 'es'
      dayjs.locale(currentLocale)
    })()
  }, [locale])

  /**
   * Page transition loader
   */
  useEffect(() => {
    events.on('routeChangeStart', () => setLoadingPage(true))
    events.on('routeChangeComplete', () => setLoadingPage(false))

    return () => events.off('routeChangeComplete', () => setLoadingPage(false))
  }, [events])

  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>

      <PrimeReactProvider>
        <SessionProvider refetchInterval={refetchInterval} session={session}>
          <AppContextProvider>
            {children}
            <BlockUI
              fullScreen
              blocked={loadingPage}
              template={logoSpinner ?? <Spinner />}
            />
          </AppContextProvider>
        </SessionProvider>
      </PrimeReactProvider>
    </>
  )
}
